/**
 * Draws one frame of the comparison video onto a 2D context.
 *
 * This is the single rendering implementation — the form preview, the playback
 * preview and the MP4 encoder all call it, so none of them can drift from the
 * others.
 *
 * Fonts are a deliberate system stack rather than a webfont: canvas text needs
 * the face to be loaded *before* the first draw, and a font that arrives late
 * would silently change frame sizing partway through an export.
 */
import { containRect, coverRect, watermarkRect, type Rect } from "./layout";
import { poseAt, type Scene } from "./scene";
import { readableTextOn } from "./theme";
import { findActiveLine, findActiveWordIndex } from "./words";
import type { CaptionWord, PoseKind } from "./types";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

const CARD_FILL = "#ffffff";
const CARD_BORDER = "#e2e8f0";
const CARD_SHADOW = "rgba(15, 23, 42, 0.10)";
const CAPTION_PILL = "rgba(15, 23, 42, 0.92)";
const CAPTION_TEXT = "#ffffff";
const WATERMARK_PILL = "rgba(0, 0, 0, 0.55)";

export function drawFrame({
	ctx,
	scene,
	timeMs,
	poseOverride,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
	timeMs: number;
	poseOverride?: PoseKind;
}): void {
	const { layout } = scene;
	const { width, height } = layout.canvas;

	ctx.save();
	ctx.clearRect(0, 0, width, height);

	drawBackground({ ctx, scene });

	drawCard({ ctx, scene, rect: layout.thisCard, card: scene.thisCard });
	drawCard({ ctx, scene, rect: layout.thatCard, card: scene.thatCard });

	drawLabel({ ctx, scene, rect: layout.thisCard, text: scene.thisCard.label });
	drawLabel({ ctx, scene, rect: layout.thatCard, text: scene.thatCard.label });

	drawCharacter({
		ctx,
		scene,
		pose: poseOverride ?? poseAt({ scene, timeMs }),
	});

	if (scene.showCaptions) drawCaption({ ctx, scene, timeMs });

	drawWatermark({ ctx, scene });

	ctx.restore();
}

function drawBackground({
	ctx,
	scene,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
}): void {
	const { width, height } = scene.layout.canvas;
	ctx.fillStyle = scene.backgroundColor;
	ctx.fillRect(0, 0, width, height);

	const image = scene.backgroundImage;
	if (!image) return;

	const rect = coverRect({
		source: { width: image.naturalWidth, height: image.naturalHeight },
		within: { x: 0, y: 0, w: width, h: height },
	});
	ctx.drawImage(image, rect.x, rect.y, rect.w, rect.h);
}

function drawCard({
	ctx,
	scene,
	rect,
	card,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
	rect: Rect;
	card: Scene["thisCard"];
}): void {
	const { cardRadius, cardBorder, cardPadding } = scene.layout;

	// The card is drawn even when empty: without the frame the two options blur
	// into one another against a plain background.
	ctx.save();
	ctx.shadowColor = CARD_SHADOW;
	ctx.shadowBlur = cardRadius * 0.9;
	ctx.shadowOffsetY = cardRadius * 0.2;
	ctx.fillStyle = CARD_FILL;
	roundRect({ ctx, rect, radius: cardRadius });
	ctx.fill();
	ctx.restore();

	ctx.save();
	ctx.strokeStyle = CARD_BORDER;
	ctx.lineWidth = cardBorder;
	roundRect({
		ctx,
		rect: inset({ rect, by: cardBorder / 2 }),
		radius: cardRadius - cardBorder / 2,
	});
	ctx.stroke();
	ctx.restore();

	if (!card.image) return;

	const inner = inset({ rect, by: cardPadding });
	const fitted = containRect({
		source: {
			width: card.image.naturalWidth,
			height: card.image.naturalHeight,
		},
		within: inner,
	});
	ctx.drawImage(card.image, fitted.x, fitted.y, fitted.w, fitted.h);
}

function drawLabel({
	ctx,
	scene,
	rect,
	text,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
	rect: Rect;
	text: string;
}): void {
	const label = text.trim();
	if (!label) return;

	const { labelFontPx, labelTop } = scene.layout;
	ctx.save();
	ctx.font = `bold ${labelFontPx}px ${FONT_STACK}`;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	const paddingX = labelFontPx * 0.66;
	const paddingY = labelFontPx * 0.42;
	const textWidth = Math.min(ctx.measureText(label).width, rect.w * 1.1);
	const chip: Rect = {
		x: rect.x + rect.w / 2 - textWidth / 2 - paddingX,
		y: labelTop,
		w: textWidth + paddingX * 2,
		h: labelFontPx + paddingY * 2,
	};

	ctx.fillStyle = scene.themeColor;
	roundRect({ ctx, rect: chip, radius: chip.h / 2 });
	ctx.fill();

	ctx.fillStyle = readableTextOn({ color: scene.themeColor });
	ctx.fillText(label, rect.x + rect.w / 2, chip.y + chip.h / 2, textWidth);
	ctx.restore();
}

function drawCharacter({
	ctx,
	scene,
	pose,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
	pose: PoseKind;
}): void {
	const image = scene.poseImages[pose] ?? scene.poseImages.idle;
	if (!image) return;

	// Every pose shares one source size, so contain-fitting gives an identical
	// rect for each — the body stays put and only the arm moves.
	const fitted = containRect({
		source: { width: image.naturalWidth, height: image.naturalHeight },
		within: scene.layout.character,
	});
	ctx.drawImage(image, fitted.x, fitted.y, fitted.w, fitted.h);
}

/** Type is never shrunk below this fraction of its layout size. */
const CAPTION_MIN_SCALE = 0.62;

interface PlacedWord {
	word: CaptionWord;
	index: number;
	x: number;
	width: number;
}

/**
 * Captions are always ONE line.
 *
 * Wrapping to two lines makes the pill both wide and tall enough to dominate
 * the frame, which is what made earlier videos look cramped. `words.ts` already
 * groups into short lines; anything that still overruns is shrunk to fit rather
 * than wrapped.
 */
function drawCaption({
	ctx,
	scene,
	timeMs,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
	timeMs: number;
}): void {
	const line = findActiveLine({ lines: scene.captions, timeMs });
	if (!line || line.words.length === 0) return;

	const { captionFontPx, captionMaxWidth, captionBottom, canvas } = scene.layout;
	const activeIndex = findActiveWordIndex({ line, timeMs });

	ctx.save();
	ctx.textAlign = "left";
	ctx.textBaseline = "middle";

	let fontPx = captionFontPx;
	ctx.font = `bold ${fontPx}px ${FONT_STACK}`;
	let placed = placeWords({ ctx, words: line.words });

	if (placed.width > captionMaxWidth) {
		fontPx = Math.max(
			captionFontPx * CAPTION_MIN_SCALE,
			(captionFontPx * captionMaxWidth) / placed.width,
		);
		ctx.font = `bold ${fontPx}px ${FONT_STACK}`;
		placed = placeWords({ ctx, words: line.words });
	}

	const paddingX = fontPx * 0.72;
	const paddingY = fontPx * 0.46;
	const lineHeight = fontPx * 1.34;

	const pill: Rect = {
		x: canvas.width / 2 - placed.width / 2 - paddingX,
		y: canvas.height - captionBottom - lineHeight - paddingY,
		w: placed.width + paddingX * 2,
		h: lineHeight + paddingY * 2,
	};

	ctx.fillStyle = CAPTION_PILL;
	roundRect({ ctx, rect: pill, radius: fontPx * 0.55 });
	ctx.fill();

	const left = canvas.width / 2 - placed.width / 2;
	const centerY = pill.y + pill.h / 2;
	const highlightText = readableTextOn({ color: scene.themeColor });

	for (const entry of placed.words) {
		const x = left + entry.x;
		const active = entry.index === activeIndex;

		if (active) {
			// The highlight is a box around the word being spoken right now.
			const box: Rect = {
				x: x - fontPx * 0.18,
				y: centerY - fontPx * 0.66,
				w: entry.width + fontPx * 0.36,
				h: fontPx * 1.32,
			};
			ctx.fillStyle = scene.themeColor;
			roundRect({ ctx, rect: box, radius: fontPx * 0.26 });
			ctx.fill();
		}

		// The rest of the line sits on the dark pill; only the highlighted word
		// sits on the accent, so only it follows the accent's readable colour.
		ctx.fillStyle = active ? highlightText : CAPTION_TEXT;
		ctx.fillText(entry.word.text, x, centerY);
	}

	ctx.restore();
}

function placeWords({
	ctx,
	words,
}: {
	ctx: CanvasRenderingContext2D;
	words: CaptionWord[];
}): { words: PlacedWord[]; width: number } {
	const spaceWidth = ctx.measureText(" ").width;
	const placed: PlacedWord[] = [];
	let cursor = 0;

	words.forEach((word, index) => {
		const width = ctx.measureText(word.text).width;
		placed.push({ word, index, x: cursor, width });
		cursor += width + spaceWidth;
	});

	return { words: placed, width: Math.max(0, cursor - spaceWidth) };
}

function drawWatermark({
	ctx,
	scene,
}: {
	ctx: CanvasRenderingContext2D;
	scene: Scene;
}): void {
	const rect = watermarkRect({
		layout: scene.layout,
		corner: scene.watermarkCorner,
	});

	const isLeft =
		scene.watermarkCorner === "top-left" ||
		scene.watermarkCorner === "bottom-left";
	const isTop =
		scene.watermarkCorner === "top-left" ||
		scene.watermarkCorner === "top-right";

	if (scene.watermarkImage) {
		const fitted = containRect({
			source: {
				width: scene.watermarkImage.naturalWidth,
				height: scene.watermarkImage.naturalHeight,
			},
			within: rect,
		});
		// Contain centres the image inside the box, which pushes a square logo
		// far off its corner — the box is wider than it is tall. Pin it to the
		// corner instead so the gap matches the inset in both directions.
		const x = isLeft ? rect.x : rect.x + rect.w - fitted.w;
		const y = isTop ? rect.y : rect.y + rect.h - fitted.h;

		ctx.save();
		ctx.globalAlpha = 0.9;
		ctx.drawImage(scene.watermarkImage, x, y, fitted.w, fitted.h);
		ctx.restore();
		return;
	}

	if (!scene.watermarkText) return;

	const fontPx = scene.layout.watermarkFontPx;
	ctx.save();
	ctx.font = `bold ${fontPx}px ${FONT_STACK}`;
	ctx.textBaseline = "middle";

	const paddingX = fontPx * 0.6;
	const paddingY = fontPx * 0.4;
	const textWidth = ctx.measureText(scene.watermarkText).width;
	const pillH = fontPx + paddingY * 2;
	// Pinned to the corner in both directions, exactly like the image above.
	// Centring it in the box instead left the gap to the frame edge larger than
	// the inset, and by a different amount than across.
	const pill: Rect = {
		x: isLeft ? rect.x : rect.x + rect.w - textWidth - paddingX * 2,
		y: isTop ? rect.y : rect.y + rect.h - pillH,
		w: textWidth + paddingX * 2,
		h: pillH,
	};

	ctx.fillStyle = WATERMARK_PILL;
	roundRect({ ctx, rect: pill, radius: pill.h / 2 });
	ctx.fill();

	ctx.fillStyle = "#ffffff";
	ctx.textAlign = "left";
	ctx.fillText(scene.watermarkText, pill.x + paddingX, pill.y + pill.h / 2);
	ctx.restore();
}

function roundRect({
	ctx,
	rect,
	radius,
}: {
	ctx: CanvasRenderingContext2D;
	rect: Rect;
	radius: number;
}): void {
	const limit = Math.max(0, Math.min(radius, rect.w / 2, rect.h / 2));
	ctx.beginPath();
	ctx.roundRect(rect.x, rect.y, rect.w, rect.h, limit);
}

function inset({ rect, by }: { rect: Rect; by: number }): Rect {
	return {
		x: rect.x + by,
		y: rect.y + by,
		w: Math.max(1, rect.w - by * 2),
		h: Math.max(1, rect.h - by * 2),
	};
}
